import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProduct } from '../context/ProductContext';
import { settingsApi, dealsApi, productsApi } from '../services/api';
import {
  Users,
  Phone,
  FileText,
  Trophy,
  TrendingUp,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  Package
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const STAGE_COLORS = {
  new: '#94A3B8',
  contacted: '#3B82F6',
  booked: '#8B5CF6',
  discovery_done: '#6366F1',
  proposal_draft: '#F59E0B',
  proposal_sent: '#F97316',
  closed_won: '#10B981',
  closed_lost: '#EF4444'
};

const STAGE_LABELS = {
  new: 'New',
  contacted: 'Contacted',
  booked: 'Booked',
  discovery_done: 'Discovery Done',
  proposal_draft: 'Proposal Draft',
  proposal_sent: 'Proposal Sent',
  closed_won: 'Won',
  closed_lost: 'Lost'
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { products } = useProduct();
  const [metrics, setMetrics] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [perfByProduct, setPerfByProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDashboardData = async () => {
    try {
      const [metricsRes, pipelineRes, perfRes] = await Promise.all([
        settingsApi.getDashboardMetrics(),
        dealsApi.getPipelineSummary(),
        productsApi.performanceDashboard().catch(() => ({ data: { products: [] } }))
      ]);
      setMetrics(metricsRes.data);
      setPipeline(pipelineRes.data);
      setPerfByProduct(perfRes.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const MetricCard = ({ title, value, icon: Icon, trend, color = 'blue', onClick }) => (
    <Card 
      className="metric-card cursor-pointer hover:border-slate-300 transition-colors"
      onClick={onClick}
      data-testid={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
            <p className="text-3xl font-bold tabular-nums">{value}</p>
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {trend >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span>{Math.abs(trend)}% this month</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg bg-${color}-100`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-8">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const stageData = Object.entries(metrics?.leads_by_stage || {}).map(([stage, count]) => ({
    name: STAGE_LABELS[stage] || stage,
    value: count,
    color: STAGE_COLORS[stage] || '#94A3B8'
  }));

  const sourceData = Object.entries(metrics?.leads_by_source || {}).map(([source, count]) => ({
    name: source.charAt(0).toUpperCase() + source.slice(1).replace('_', ' '),
    count
  }));

  return (
    <div className="p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-slate-500 mt-1">Here's what's happening with your sales pipeline</p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Leads"
          value={metrics?.total_leads || 0}
          icon={Users}
          color="blue"
          onClick={() => navigate('/leads')}
        />
        <MetricCard
          title="Booked Calls"
          value={metrics?.booked_calls || 0}
          icon={Phone}
          color="purple"
          onClick={() => navigate('/leads?stage=booked')}
        />
        <MetricCard
          title="Proposals Sent"
          value={metrics?.proposals_sent || 0}
          icon={FileText}
          color="orange"
          onClick={() => navigate('/proposals')}
        />
        <MetricCard
          title="Deals Won"
          value={metrics?.deals_won || 0}
          icon={Trophy}
          color="emerald"
          onClick={() => navigate('/leads?stage=closed_won')}
        />
      </div>

      {/* Second Row - Revenue & Wallet */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="metric-card col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Revenue Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-4 mb-6">
              <span className="text-4xl font-bold tabular-nums">
                {formatCurrency(metrics?.revenue_closed)}
              </span>
              <span className="text-slate-500">Total closed revenue</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Conversion Rate</p>
                <p className="text-2xl font-bold text-emerald-600 tabular-nums">
                  {metrics?.conversion_rate || 0}%
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Win Rate</p>
                <p className="text-2xl font-bold text-blue-600 tabular-nums">
                  {pipeline?.win_rate || 0}%
                </p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Pipeline Value</p>
                <p className="text-2xl font-bold text-purple-600 tabular-nums">
                  {formatCurrency(pipeline?.total_pipeline_value)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metric-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Wallet className="w-5 h-5 text-blue-600" />
              AI Wallet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <p className="text-sm text-slate-500">Available Credits</p>
              <p className="text-3xl font-bold tabular-nums">
                {metrics?.wallet_balance?.toFixed(0) || 0}
              </p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg mb-4">
              <p className="text-sm text-slate-500">Used This Month</p>
              <p className="text-lg font-semibold tabular-nums">
                {metrics?.wallet_usage_this_month?.toFixed(0) || 0} credits
              </p>
            </div>
            <Button 
              className="w-full btn-primary"
              onClick={() => navigate('/wallet')}
              data-testid="topup-wallet-btn"
            >
              Top Up Wallet
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Funnel */}
        <Card className="metric-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              Pipeline by Stage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
                <BarChart data={pipeline?.funnel || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" stroke="#94A3B8" fontSize={12} />
                  <YAxis 
                    type="category" 
                    dataKey="stage" 
                    stroke="#94A3B8" 
                    fontSize={12}
                    width={100}
                    tickFormatter={(value) => STAGE_LABELS[value] || value}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar 
                    dataKey="count" 
                    fill="#3B82F6"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Leads by Source */}
        <Card className="metric-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Leads by Source</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={300} minHeight={200}>
                <PieChart>
                  <Pie
                    data={stageData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {stageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              {stageData.slice(0, 6).map((item) => (
                <div key={item.name} className="flex items-center gap-2 text-xs">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-slate-600">{item.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance by Product / Service */}
      {perfByProduct && perfByProduct.products && perfByProduct.products.length > 0 && (
        <Card className="mt-6" data-testid="performance-by-product-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-500" />
              Performance by Product / Service
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/products')}>
              View all products
            </Button>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right">Active Leads</th>
                  <th className="px-3 py-2 text-right">AI Actions</th>
                  <th className="px-3 py-2 text-right">Pending</th>
                  <th className="px-3 py-2 text-right">Booked</th>
                  <th className="px-3 py-2 text-right">Proposals</th>
                  <th className="px-3 py-2 text-right">Won</th>
                  <th className="px-3 py-2 text-right">Conv. Rate</th>
                  <th className="px-3 py-2 text-right">Credits</th>
                  <th className="px-3 py-2 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {perfByProduct.products.map(row => (
                  <tr
                    key={row.product_id}
                    className="border-t hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/products/${row.product_id}`)}
                    data-testid={`perf-product-row-${row.product_code}`}
                  >
                    <td className="px-3 py-2 font-medium">{row.product_name}</td>
                    <td className="px-3 py-2 text-right">{row.active_leads}</td>
                    <td className="px-3 py-2 text-right">{row.ai_actions}</td>
                    <td className="px-3 py-2 text-right">{row.pending_actions}</td>
                    <td className="px-3 py-2 text-right">{row.booked_calls}</td>
                    <td className="px-3 py-2 text-right">{row.proposals}</td>
                    <td className="px-3 py-2 text-right font-medium text-emerald-600">{row.won_deals}</td>
                    <td className="px-3 py-2 text-right">{row.conversion_rate}%</td>
                    <td className="px-3 py-2 text-right text-slate-600">{(row.credits_used || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-semibold">${(row.revenue || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
